import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { FaChevronRight } from 'react-icons/fa'
import api from '../../../helpers/apiRequester'
import { CollapsibleSection } from '../omnibusShared/collapsibleSection'
import styles from './styles.module.scss'
import {
    ACCESS_KEYS_PATH,
    AccessDetail,
    AccessKeyInfo,
    AccessRow,
    KeyCell,
    ModeBadge,
    OutcomeBadge,
    Pill,
    accessDetailPath,
    batchAccessPath,
    batchLabel,
    formatBytes,
    formatDateTime,
    formatDuration,
    formatUa,
    handleAdminApiError,
} from './shared'

interface BatchInfo {
    batch_group: string | null
    introducer: string | null
    documents_disabled: boolean
}

interface DetailResponse {
    access: AccessDetail
    batch: BatchInfo
    accessKey: AccessKeyInfo | null
    relatedByBundle: AccessRow[]
    relatedByIp: AccessRow[]
}

const Field = ({
    label,
    value,
    mono,
    wide,
}: {
    label: string
    value: React.ReactNode
    mono?: boolean
    wide?: boolean
}) => (
    <div className={`${styles.detailField} ${wide ? styles.detailWide : ''}`}>
        <dt>{label}</dt>
        <dd className={mono ? styles.mono : undefined}>
            {value === null || value === undefined || value === '' ? <span className={styles.muted}>—</span> : value}
        </dd>
    </div>
)

const RelatedTable = ({ rows, currentId }: { rows: AccessRow[]; currentId: number }) => {
    const navigate = useNavigate()
    if (rows.length === 0) return <p className={styles.note}>None recorded.</p>
    return (
        <div className={styles.tableWrap}>
            <table className={styles.table}>
                <thead>
                    <tr>
                        <th>When</th>
                        <th>Outcome</th>
                        <th>Key</th>
                        <th>Batch / case</th>
                        <th>Requester</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r) => {
                        const isCurrent = r.id === currentId
                        return (
                            <tr
                                key={r.id}
                                className={isCurrent ? styles.current : styles.clickable}
                                onClick={() => !isCurrent && navigate(accessDetailPath(r.id))}
                                title={isCurrent ? 'This access' : 'Open this access'}
                            >
                                <td>
                                    <div className={styles.stackCell}>
                                        <span className={styles.stackPrimary}>{formatDateTime(r.accessed_at)}</span>
                                        <span className={styles.stackSecondary}>
                                            <ModeBadge mode={r.access_mode} />
                                            {isCurrent && <span className={styles.stackMeta}>· this record</span>}
                                        </span>
                                    </div>
                                </td>
                                <td>
                                    <OutcomeBadge outcome={r.outcome} />
                                </td>
                                <td>
                                    <KeyCell row={r} />
                                </td>
                                <td>
                                    <div className={styles.stackCell}>
                                        <span className={styles.stackPrimary}>
                                            {r.batch_group ? (
                                                <span className={styles.refCode}>{r.batch_group}</span>
                                            ) : (
                                                <span className={styles.muted}>{batchLabel(null)}</span>
                                            )}
                                        </span>
                                        <span className={`${styles.stackSecondary} ${styles.mono}`}>
                                            {r.case_ref || '—'}
                                        </span>
                                    </div>
                                </td>
                                <td>
                                    <div className={styles.stackCell}>
                                        <span className={`${styles.stackPrimary} ${styles.mono}`}>
                                            {r.ip_address || '—'}
                                        </span>
                                        <span className={styles.stackSecondary}>
                                            {r.location || <span className={styles.stackMeta}>Unknown location</span>}
                                        </span>
                                    </div>
                                </td>
                            </tr>
                        )
                    })}
                </tbody>
            </table>
        </div>
    )
}

/**
 * Level 3: everything we know about one documentPack request.
 */
export const DocumentBundleAccessDetailView = ({
    onLoaded,
}: {
    onLoaded?: (access: AccessDetail) => void
}) => {
    const { accessId } = useParams<{ accessId: string }>()
    const navigate = useNavigate()
    const [data, setData] = useState<DetailResponse | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    const load = useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            const { data: res } = await api({ url: '/document-access-log-detail', data: { id: accessId } })
            if (!res?.success) throw new Error(res?.message || 'Failed to load access record')
            setData(res)
            onLoaded?.(res.access)
        } catch (err: any) {
            if (handleAdminApiError(err, navigate)) return
            setError(err?.response?.data?.message || err?.message || 'Failed to load access record')
        } finally {
            setLoading(false)
        }
    }, [accessId, navigate, onLoaded])

    useEffect(() => {
        load()
    }, [load])

    if (loading) return <div className={styles.emptyState}>Loading access record…</div>
    if (error || !data) {
        return (
            <div className={styles.errorBanner}>
                {error || 'Access record not found.'}{' '}
                <button type="button" className={styles.linkBtn} onClick={load}>
                    Retry
                </button>
            </div>
        )
    }

    const { access, batch, accessKey, relatedByBundle, relatedByIp } = data
    const isRaw = access.access_mode === 'raw'
    const geo = [access.geo_city, access.geo_region, access.geo_country].filter(Boolean).join(', ')
    const coords =
        access.geo_lat !== null && access.geo_lng !== null && access.geo_lat !== undefined
            ? `${Number(access.geo_lat).toFixed(4)}, ${Number(access.geo_lng).toFixed(4)}`
            : null
    const uaSummary = formatUa(access.ua)
    const completion =
        access.content_length && access.bytes_sent !== null
            ? Math.min(100, Math.round((Number(access.bytes_sent) / Number(access.content_length)) * 100))
            : null

    return (
        <div className={styles.wrap}>
            <div className={styles.pageHeader}>
                <div>
                    <h2 className={styles.pageTitle}>
                        Access #{access.id}
                        <ModeBadge mode={access.access_mode} />
                        <OutcomeBadge outcome={access.outcome} />
                    </h2>
                    <div className={styles.headerMeta}>
                        <span className={styles.metaText}>{formatDateTime(access.accessed_at)}</span>
                        <Link className={styles.metaLink} to={batchAccessPath(access.batch_group)}>
                            {batchLabel(access.batch_group)} <FaChevronRight size={10} />
                        </Link>
                        {access.ip_address && <span className={`${styles.metaText} ${styles.mono}`}>{access.ip_address}</span>}
                        {isRaw && <KeyCell row={access} />}
                    </div>
                </div>
            </div>

            {(isRaw || accessKey) && (
                <CollapsibleSection
                    title="Access key"
                    count={access.access_key_type === 'master' ? 'master' : accessKey ? accessKey.label : 'none'}
                    actions={
                        <Link className={`${styles.secondaryBtn} ${styles.smallBtn}`} to={ACCESS_KEYS_PATH}>
                            Manage keys
                        </Link>
                    }
                >
                    {access.access_key_type === 'master' ? (
                        <>
                            <p className={styles.note}>
                                {access.outcome === 'success'
                                    ? 'Authorised with the shared legacy master key.'
                                    : 'Presented the shared legacy master key while it was switched off and was refused. Whoever is using this link should be issued a named alias key.'}{' '}
                                The master key can be switched on or off from the Keys page.
                            </p>
                            <dl className={styles.detailGrid}>
                                <Field label="Label" value={accessKey?.label ?? 'Master key'} />
                                <Field
                                    label="Key status now"
                                    value={
                                        accessKey ? (
                                            accessKey.enabled ? (
                                                <Pill tone="ok">Enabled</Pill>
                                            ) : (
                                                <Pill tone="bad">Disabled</Pill>
                                            )
                                        ) : (
                                            <Pill tone="bad">Disabled (not yet seeded)</Pill>
                                        )
                                    }
                                />
                            </dl>
                        </>
                    ) : accessKey?.systemRole === 'dashboard' ? (
                        <>
                            <p className={styles.note}>
                                Opened from the Sentinel dashboard (funding client details or omnibus claim drawer) using
                                the built-in <Pill tone="blue">Dashboard</Pill> key.
                            </p>
                            <dl className={styles.detailGrid}>
                                <Field label="Label" value={accessKey.label} />
                                <Field label="Key status" value={<Pill tone="ok">Always enabled</Pill>} />
                            </dl>
                        </>
                    ) : accessKey ? (
                        <dl className={styles.detailGrid}>
                            <Field label="Label" value={accessKey.label} />
                            <Field label="Holder" value={accessKey.holderName} />
                            <Field label="Holder email" value={accessKey.holderEmail} />
                            <Field label="Organisation" value={accessKey.organisation} />
                            <Field
                                label="Key status"
                                value={accessKey.enabled ? <Pill tone="ok">Enabled</Pill> : <Pill tone="bad">Disabled</Pill>}
                            />
                        </dl>
                    ) : (
                        <p className={styles.note}>
                            No valid key was presented for this request
                            {access.query_string ? (
                                <>
                                    {' '}
                                    (query: <span className={styles.mono}>{access.query_string}</span>)
                                </>
                            ) : null}
                            .
                        </p>
                    )}
                </CollapsibleSection>
            )}

            <CollapsibleSection title="Request" count={access.http_status ? `HTTP ${access.http_status}` : undefined}>
                <dl className={styles.detailGrid}>
                    <Field label="Requested at" value={formatDateTime(access.accessed_at)} />
                    <Field label="Completed at" value={formatDateTime(access.completed_at)} />
                    <Field label="Duration" value={formatDuration(access.duration_ms)} />
                    <Field label="Mode" value={<ModeBadge mode={access.access_mode} />} />
                    <Field label="Outcome" value={<OutcomeBadge outcome={access.outcome} />} />
                    <Field label="HTTP status" value={access.http_status} mono />
                    <Field label="Query string" value={access.query_string} mono wide />
                    <Field label="Referer" value={access.referer} mono wide />
                </dl>
                {access.error_message && <div className={styles.errorBlock}>{access.error_message}</div>}
            </CollapsibleSection>

            <CollapsibleSection title="Bundle" count={access.case_ref || undefined}>
                <dl className={styles.detailGrid}>
                    <Field
                        label="Funding batch"
                        value={
                            access.batch_group ? (
                                <>
                                    <Link className={styles.rowLink} to={`/funding-batch/${encodeURIComponent(access.batch_group)}`}>
                                        {access.batch_group}
                                    </Link>{' '}
                                    {batch.documents_disabled && <Pill tone="warn">Disabled</Pill>}
                                </>
                            ) : (
                                <span className={styles.muted}>Unassigned</span>
                            )
                        }
                    />
                    <Field label="Introducer" value={batch.introducer} />
                    <Field label="Case ref" value={access.case_ref} mono />
                    <Field
                        label="Funder client"
                        value={
                            access.funder_client_id ? (
                                <Link className={styles.rowLink} to={`/funding-client/${access.funder_client_id}`}>
                                    {access.funder_client_id}
                                </Link>
                            ) : null
                        }
                    />
                    <Field label="Doc type" value={access.doc_type} mono />
                    <Field label="File" value={access.file_name} mono wide />
                    <Field label="S3 key" value={access.s3_key} mono wide />
                </dl>
            </CollapsibleSection>

            <CollapsibleSection title="Requester" count={geo || undefined}>
                <dl className={styles.detailGrid}>
                    <Field label="IP address" value={access.ip_address} mono />
                    <Field label="Location" value={geo || null} />
                    <Field label="Timezone" value={access.geo_timezone} />
                    <Field
                        label="Coordinates"
                        value={
                            coords ? (
                                <a
                                    className={styles.rowLink}
                                    href={`https://www.openstreetmap.org/?mlat=${access.geo_lat}&mlon=${access.geo_lng}#map=11/${access.geo_lat}/${access.geo_lng}`}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    {coords}
                                </a>
                            ) : null
                        }
                        mono
                    />
                    <Field label="Browser / OS" value={uaSummary === '—' ? null : uaSummary} />
                    <Field label="Device" value={access.ua?.device} />
                    <Field label="User agent" value={access.user_agent} mono wide />
                </dl>
            </CollapsibleSection>

            <CollapsibleSection
                title="Transfer"
                count={isRaw && completion !== null ? `${completion}%` : undefined}
                defaultOpen={isRaw}
            >
                {isRaw ? (
                    <dl className={styles.detailGrid}>
                        <Field label="Content length" value={formatBytes(access.content_length)} />
                        <Field label="Bytes sent" value={formatBytes(access.bytes_sent)} />
                        <Field label="Complete" value={completion === null ? null : `${completion}%`} />
                        <Field label="Range header" value={access.range_header} mono />
                    </dl>
                ) : (
                    <p className={styles.note}>
                        Viewer requests are redirected to viewer.sentinellab.ai; the PDF itself is served by the viewer,
                        so no transfer data is recorded here.
                    </p>
                )}
            </CollapsibleSection>

            <CollapsibleSection title="Other accesses of this bundle" count={relatedByBundle.length}>
                <RelatedTable rows={relatedByBundle} currentId={access.id} />
            </CollapsibleSection>

            <CollapsibleSection
                title={`Other accesses from ${access.ip_address || 'this IP'}`}
                count={relatedByIp.length}
            >
                <RelatedTable rows={relatedByIp} currentId={access.id} />
            </CollapsibleSection>
        </div>
    )
}

export default DocumentBundleAccessDetailView
